import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from './msal';
import { setAuthContext } from './api-client';
import { useMSALInit } from './msal-init-context';

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  token: string | null;
  user: User | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const { msalInitialized } = useMSALInit();
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  // Track stabilization after HandleRedirect completes (mobile race condition fix)
  const [isStabilizing, setIsStabilizing] = useState(false);
  // Track if we've checked accounts array at least once (prevents premature auth check on mobile)
  const [hasCheckedAccounts, setHasCheckedAccounts] = useState(false);
  const prevInProgressRef = useRef<InteractionStatus>(inProgress);

  const isAuthenticated = !!account && inProgress === InteractionStatus.None;

  // Loading is true until:
  // 1. MSAL has finished initialization AND
  // 2. We've confirmed the accounts state (either found accounts or confirmed none exist) AND
  // 3. No active interaction in progress AND
  // 4. Not currently stabilizing
  const loading = !msalInitialized || !hasCheckedAccounts || inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect || isStabilizing;

  const login = useCallback(async () => {
    // Detect if mobile browser - use redirect instead of popup
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;

    try {
      if (isMobile) {
        // Check if already authenticated (prevents duplicate login)
        if (isAuthenticated) {
          return;
        }

        // Only block if login is actively in progress
        if (inProgress === InteractionStatus.Login) {
          throw new Error('Authentication is already in progress. Please wait.');
        }

        // CRITICAL: Clear ALL MSAL state before login to prevent "interaction_in_progress" errors

        // Clear sessionStorage
        const sessionKeys = Object.keys(sessionStorage).filter(key => key.startsWith('msal'));
        sessionKeys.forEach(key => sessionStorage.removeItem(key));

        // Clear localStorage
        const localKeys = Object.keys(localStorage).filter(key => key.startsWith('msal'));
        localKeys.forEach(key => localStorage.removeItem(key));

        // Clear MSAL's internal cache
        try {
          await instance.clearCache();
        } catch (cacheError) {
          console.error('[Auth] Error clearing MSAL cache:', cacheError);
        }

        // Clear cookies
        const cookies = document.cookie.split(';');
        const msalCookies = cookies.filter(cookie => {
          const name = cookie.split('=')[0].trim();
          return name.includes('msal') || name.includes('interaction');
        });
        msalCookies.forEach(cookie => {
          const name = cookie.split('=')[0].trim();
          const deletions = [
            `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`,
            `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`,
            `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`,
          ];
          deletions.forEach(deletion => document.cookie = deletion);
        });

        // Small delay to ensure cache clearing completes
        await new Promise(resolve => setTimeout(resolve, 50));

        await instance.loginRedirect(loginRequest);
      } else {
        await instance.loginPopup(loginRequest);
      }
    } catch (error) {
      console.error('[Auth] Login failed:', error);
      throw error;
    }
  }, [instance, inProgress, accounts, isAuthenticated]);

  const logout = useCallback(async () => {
    // Use same detection for logout
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;

    // Clear local state first
    setToken(null);
    setUser(null);

    if (isMobile) {
      // Clear sessionStorage
      const sessionKeys = Object.keys(sessionStorage).filter(key => key.startsWith('msal'));
      sessionKeys.forEach(key => sessionStorage.removeItem(key));

      // Clear localStorage
      const localKeys = Object.keys(localStorage).filter(key => key.startsWith('msal'));
      localKeys.forEach(key => localStorage.removeItem(key));

      // Clear MSAL's internal cache
      try {
        await instance.clearCache();
      } catch (error) {
        console.error('[Auth] Error clearing MSAL cache:', error);
      }

      // Clear cookies
      const cookies = document.cookie.split(';');
      cookies.forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        if (name.startsWith('msal') || name.includes('interaction')) {
          const deletions = [
            `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`,
            `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`,
            `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`,
          ];
          deletions.forEach(deletion => document.cookie = deletion);
        }
      });

      await instance.logoutRedirect();
    } else {
      await instance.logoutPopup();
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
      return response.accessToken;
    } catch (error) {
      // If silent token acquisition fails, use interactive method
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;

      try {
        if (isMobile) {
          // On mobile, use redirect for token acquisition
          await instance.acquireTokenRedirect({
            ...loginRequest,
            account: account,
          });
          // Note: This will redirect the page, so we won't return from here
          return null;
        } else {
          // On desktop, use popup for token acquisition
          const response = await instance.acquireTokenPopup({
            ...loginRequest,
            account: account,
          });
          return response.accessToken;
        }
      } catch (interactiveError) {
        return null;
      }
    }
  }, [instance, account]);

  // Mark accounts as checked when MSAL initialization is complete AND we have definitive state
  useEffect(() => {
    if (msalInitialized && !hasCheckedAccounts) {
      if (accounts.length > 0) {
        setHasCheckedAccounts(true);
      } else if (inProgress === InteractionStatus.None) {
        setHasCheckedAccounts(true);
      }
    }
  }, [msalInitialized, accounts.length, inProgress, hasCheckedAccounts]);

  // Stabilization effect: Add delay after HandleRedirect completes to ensure accounts array is populated
  // This fixes mobile race condition where inProgress becomes None before accounts is ready
  useEffect(() => {
    let stabilizationTimer: number;

    // Stabilize if we just finished redirect OR if we're at None without account and haven't checked yet
    const justFinishedRedirect =
      prevInProgressRef.current === InteractionStatus.HandleRedirect &&
      inProgress === InteractionStatus.None &&
      !account &&
      accounts.length === 0;

    const needsStabilization =
      inProgress === InteractionStatus.None &&
      !account &&
      accounts.length === 0 &&
      !hasCheckedAccounts;

    if (justFinishedRedirect || needsStabilization) {
      // Just finished HandleRedirect or page load but no account yet - wait for it to load
      setIsStabilizing(true);
      stabilizationTimer = setTimeout(() => {
        setIsStabilizing(false);
      }, 500); // Give mobile browsers time to populate accounts array
    } else {
      // Either still loading, or we have an account - no stabilization needed
      setIsStabilizing(false);
    }

    // Update ref for next render
    prevInProgressRef.current = inProgress;

    return () => {
      if (stabilizationTimer) {
        clearTimeout(stabilizationTimer);
      }
    };
  }, [inProgress, account, accounts.length, hasCheckedAccounts]);

  // Update token and user when account changes
  useEffect(() => {
    const updateAuthState = async () => {
      if (account) {
        const accessToken = await getAccessToken();
        setToken(accessToken);

        // Map MSAL account to our User interface
        const mappedUser: User = {
          id: account.localAccountId,
          email: account.username || '',
          name: account.name || '',
          roles: [], // Roles would need to be extracted from token claims if needed
        };
        setUser(mappedUser);
      } else {
        setToken(null);
        setUser(null);
      }
    };

    updateAuthState();
  }, [account, getAccessToken]);

  // Refresh token when tab becomes active again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && account) {
        // Tab became visible, refresh token
        const newToken = await getAccessToken();
        if (newToken) {
          setToken(newToken);
        }
      }
    };

    const handleFocus = async () => {
      if (account) {
        // Window got focus, refresh token
        const newToken = await getAccessToken();
        if (newToken) {
          setToken(newToken);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [account, getAccessToken]);

  // Set up API client with auth context
  useEffect(() => {
    setAuthContext({
      refreshAuthToken: getAccessToken,
      logout,
      token
    });
  }, [getAccessToken, logout, token]);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      login,
      logout,
      token,
      user,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};